import {
  Create,
  Edit,
  required,
  Resource,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useRedirect,
} from "react-admin";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { DrivingSchoolOtherEventCategory } from "../providers/drivingSchoolOtherEventCategoriesProvider";
import { grants } from "../backoffice.access_control";
import { Form2Theme } from "../misc/Form2";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { DefaultList } from "../misc/DefaultList";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateOtherEventCategoryForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      resource="otherEventCategories"
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Sonstige Arbeitszeit erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/otherEventCategories`);
        },
        onError: (error: Error) => {
          showErrorToast("Fehler beim Erstellen der Sonstige Arbeitszeit.");
          console.error("Failed to create drivingSchoolOtherEventCategories.", error);
        },
        meta: { drivingSchoolId },
      }}
    >
      <DrivingSchoolOtherEventCategoryForm mode="create" />
    </Create>
  );
}

function EditOtherEventCategoryForm() {
  const redirect = useRedirect();
  const category = useRecordAndSwitchDrivingSchoolIfNecessary<DrivingSchoolOtherEventCategory>((it) => {
    const { drivingSchoolId } = it;
    if (!drivingSchoolId) {
      throw new Error(`OtherEventCategory ${it.id} has no drivingSchoolId`);
    }
    return drivingSchoolId;
  }) as undefined | Defined<DrivingSchoolOtherEventCategory, "drivingSchoolId">;

  if (!category) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      resource="otherEventCategories"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${category.drivingSchoolId}/otherEventCategories`);
        },
        onError: (error: unknown) => {
          showErrorToast("Fehler beim Speichern.");
          console.error("Failed to update drivingSchoolOtherEventCategories.", error);
        },
      }}
      mutationMode="pessimistic"
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Sonstige Arbeitszeiten"
              backUrl={`/drivingSchools/${category.drivingSchoolId}/otherEventCategories`}
              title={category.name}
            />
          }
        />
      }
    >
      <DrivingSchoolOtherEventCategoryForm mode="edit" />
    </Edit>
  );
}

function CustomToolbar(props: ToolbarProps & { mode: "create" | "edit" }) {
  const { mode, ...rest } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canSave = mode === "create" ? grants.has("OtherEvent:category:create") : grants.has("OtherEvent:category:edit");
  return (
    <Toolbar {...rest} sx={{ justifyContent: "space-between" }}>
      {canSave && <SaveButton icon={<></>} />}
      {mode === "edit" && grants.has("OtherEvent:category:delete") && (
        <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/otherEventCategories`} />
      )}
    </Toolbar>
  );
}

function DrivingSchoolOtherEventCategoryForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  return (
    <Form2Theme>
      <SimpleForm
        warnWhenUnsavedChanges
        defaultValues={{ name: "", description: "", isPaid: null }}
        toolbar={<CustomToolbar mode={mode} />}
      >
        <TextInput
          source="name"
          label="Name"
          autoFocus={mode === "create"}
          validate={[required()]}
          sx={{ width: "40%" }}
        />
        <TextInput source="description" label="Beschreibung" multiline rows={2} sx={{ width: "40%" }} />
        <SelectInput
          source="isPaid"
          label="Bezahlt"
          choices={[
            { id: null, name: "Keine Auswahl" },
            { id: true, name: "Bezahlt" },
            { id: false, name: "Nicht bezahlt" },
          ]}
          sx={{ width: "40%" }}
        />
      </SimpleForm>
    </Form2Theme>
  );
}

export const drivingSchoolOtherEventCategoriesResource = (
  <Resource
    key="otherEventCategories"
    name="otherEventCategories"
    list={DefaultList}
    create={CreateOtherEventCategoryForm}
    edit={EditOtherEventCategoryForm}
    options={{ label: "Sonstige Arbeitszeiten", createTitle: "Neue Sonstige Arbeitszeit" }}
    recordRepresentation={(otherEventCategory: DrivingSchoolOtherEventCategory) => otherEventCategory.name}
  />
);
